import type { ValueFormatterFunc } from 'ag-grid-community';

import { COUNTRIES } from '../../constants/countries';
import type { DerivativeRow } from '../../types';

// Formats array values into a comma-separated string
export const listFormatter: ValueFormatterFunc<
  Partial<DerivativeRow>,
  string[]
> = (p) => {
  const uniqueValues = Array.from(new Set(p.value ?? []));

  return uniqueValues.join(', ');
};

export const numberFormat = (value: number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '--';
  }

  if (Math.abs(value) < 1e-10) {
    return '0';
  }

  const absValue = Math.abs(value);

  let formattedValue: string;

  if (absValue >= 1e6) {
    // If the value is 1 million or more
    formattedValue =
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value / 1e6) + 'M';
  } else if (absValue >= 1e3) {
    // If the value is 1 thousand or more
    formattedValue =
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value / 1e3) + 'K';
  } else {
    // For values less than 1000, round to 1 decimal place if necessary
    formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: value % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 1,
    }).format(value);
  }

  return formattedValue;
};

export const zeroFormatter: ValueFormatterFunc<
  Partial<DerivativeRow>,
  number
> = ({ value }) => {
  return numberFormat(value);
};

export const numberFormatDecimal = (
  value: number | undefined | null,
): string => {
  if (value !== undefined && value !== null && Math.abs(value) < 1e-10) {
    return '0';
  }

  if (value !== undefined && value !== null) {
    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

    return formattedValue;
  }

  return '--';
};

export const formatPercentage = (value: number | undefined | null): string => {
  if (value !== undefined && value !== null && Math.abs(value) < 1e-10) {
    return '0%';
  }

  if (value !== undefined && value !== null) {
    const percentageValue = value * 100;
    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(percentageValue);

    return `${formattedValue}%`;
  }

  return '--';
};

export const dateFormatter: ValueFormatterFunc<
  Partial<DerivativeRow>,
  string
> = (p) => {
  if (!p.value) {
    return '';
  }

  if (typeof p.value === 'string') {
    return new Date(p.value).toLocaleDateString();
  }

  return '--';
};

export const countryString = (code: string): string => {
  const country = COUNTRIES.find((c) => c.code === code);

  if (!country) {
    return code;
  }

  return country.name;
};

export const countryRenderer = (
  code: string,
  format: 'name' | 'detailed' = 'name',
) => {
  const country = COUNTRIES.find((c) => c.code === code);

  if (!country) {
    return <>{code}</>;
  }

  if (format === 'detailed') {
    return (
      <>
        <span className="">{country.name}</span>
        <span className="text-display-sm text-subtle"> ({country.code})</span>
      </>
    );
  }

  return <>{country.name}</>;
};

export const autoPluralize = (
  value: number | undefined,
  singular: string,
  plural: string,
): string => {
  return Math.abs(value || 0) === 1 ? singular : plural;
};
